import React, { useEffect } from "react";
import { SpsLabel, useSpsForm, usePatchReducer, SpsButton } from "@spscommerce/ds-react";
import { SpsPasswordInput } from "../../components/SpsPasswordInput";
import { AppRouteProps } from "../../App";
import * as v from "../../utils/validation";
import { PasswordPolicyInterface } from "../../interfaces/PasswordPolicy_interface";
import { ButtonKind, ButtonType } from "@spscommerce/ds-shared";
import { ValidationCheckInterface } from "../../interfaces/ValidationCheck_interface";
import PasswordPolicy from "../../components/PasswordPolicy";
import StatusState from "../../components/StatusState";
import { useSpsLanguage } from "@spscommerce/i18n";

export type CreateNewPasswordProps = Partial<AppRouteProps> &
    Pick<AppRouteProps, "authService" | "redirectService" | "location">;

export function CreateNewPassword({
    authService,
    redirectService,
    location,
}: CreateNewPasswordProps) {
    const { t } = useSpsLanguage();
    const [state, patchState] = usePatchReducer<{
        passwordShowing: boolean;
        error: boolean;
        errorMessage: string;
        policyError: boolean;
        success: boolean;
        submit: boolean;
        passwordPolicy: PasswordPolicyInterface;
        validationCheck: ValidationCheckInterface;
    }>({
        passwordShowing: false,
        error: false,
        errorMessage: "",
        policyError: false,
        success: false,
        submit: false,
        passwordPolicy: {
            data: {
                data: {
                    info_message: "",
                    regex_policy: {
                        lower: {
                            error: "",
                            pattern: "",
                        },
                        minlength: {
                            error: "",
                            pattern: "",
                        },
                        number: {
                            error: "",
                            pattern: "",
                        },
                        special: {
                            error: "",
                            pattern: "",
                        },
                        upper: {
                            error: "",
                            pattern: "",
                        },
                    },
                },
            },
        },
        validationCheck: {
            lower: false,
            minlength: false,
            number: false,
            special: false,
            upper: false,
        },
    });

    const { formValue, formMeta } = useSpsForm({ password: "" });

    useEffect(() => {
        getPasswordPolicy();
        // eslint-disable-next-line
    }, []);

    async function getPasswordPolicy() {
        try {
            const policy: any = await authService.getPasswordPolicy();
            let policyData: PasswordPolicyInterface = policy.data;
            patchState({ passwordPolicy: policyData });
        } catch (e) {
            patchState({ policyError: true });
        }
    }

    function validation(validator: string, currentValue: string): boolean {
        return v.validatePassword(state.passwordPolicy, validator, currentValue);
    }

    function validateAll(currentValue: string) {
        return v.validateAll(state.passwordPolicy, currentValue);
    }

    function backToLogin() {
        redirectService.backToLogin();
    }

    async function resetPassword(e: React.FormEvent) {
        e.preventDefault();
        patchState({ submit: true, error: false });
        let queryParams = new URLSearchParams(location?.search);
        let params = {
            email_address: queryParams.get("email_address"),
            reset_code: queryParams.get("reset_code"),
            new_password: formValue.password,
        };
        try {
            await authService.resetPassword(params);
            patchState({ success: true });
        } catch (e) {
            console.error(e);
            patchState({ error: true, errorMessage: e.response.data.error_description });
        }
        patchState({ submit: false });
    }

    function runValidationCheck(value: string) {
        let newStateObj: any = {};
        for (const key in state.validationCheck) {
            newStateObj[key] = validation(key, value);
        }
        patchState({ validationCheck: newStateObj });
    }

    return (
        <>
            <div className="auth-box">
                <div className="auth-logo">
                    <div className="logo"></div>
                </div>
                <div className="auth-content">
                    <form onSubmit={resetPassword}>
                        {state.success ? (
                            <>
                                <StatusState
                                    header={t("cpauth.createpw.success", {
                                        defaultValue: "Your password has been reset!",
                                    })}
                                    backToLogin={backToLogin}
                                    imgSrc="/assets/image-success.svg"
                                    alt="Success Status Icon"
                                />
                            </>
                        ) : (
                            <>
                                {state.policyError && (
                                    <>
                                        <StatusState
                                            header={t("cpauth.oops", {
                                                defaultValue:
                                                    "Oops! We're Having a Problem on Our End",
                                            })}
                                            message={t("cpauth.createaccount.prefetchPolicyError", {
                                                defaultValue:
                                                    "Cannot retrieve the password policy. Please contact customer support.",
                                            })}
                                            backToLogin={backToLogin}
                                            imgSrc="/assets/image-error-6.svg"
                                            alt="Error Status Icon"
                                        />
                                    </>
                                )}
                                {!state.policyError && (
                                    <>
                                        <h1 className="auth-content-header">
                                            {t("cpauth.createpw.create", {
                                                defaultValue: "Create a new password",
                                            })}
                                        </h1>
                                        {state.error && (
                                            <p className="auth-error">{state.errorMessage}</p>
                                        )}
                                        <SpsLabel for={formMeta.fields.password}>
                                            {t("cpauth.password", { defaultValue: "Password" })}
                                        </SpsLabel>
                                        <SpsPasswordInput
                                            value={formValue.password}
                                            formMeta={formMeta.fields.password}
                                            className="mb-2"
                                            onChange={(
                                                event: React.ChangeEvent<HTMLInputElement>
                                            ) => {
                                                runValidationCheck(event.target.value);
                                            }}
                                        />
                                        <PasswordPolicy validationCheck={state.validationCheck} />
                                    </>
                                )}
                                <SpsButton
                                    className="mr-1 mb-1 mt-4 auth-button"
                                    spinning={state.submit}
                                    disabled={!validateAll(formValue.password)}
                                    kind={ButtonKind.CONFIRM}
                                    type={ButtonType.SUBMIT}
                                >
                                    {t("cpauth.resetPassword", { defaultValue: "Reset Password" })}
                                </SpsButton>
                            </>
                        )}
                    </form>
                </div>
            </div>
        </>
    );
}
